import './_cardPayment.scss'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
    cardIcon,
    cardPaymentInstruction,
    cashIcon,
    noProductsImg,
    paymentFailedIndicator,
    supportedCards,
    vendorPlaceholderDark,
    welcome_gaming_playStation
} from '@/assets/images'
import PrimaryButton from '@/components/atoms/primaryButton/primaryButton'
import PosReceiptCard from '@/components/molecules/posReceiptCard/posReceiptCard'
import VendorBonusAmountNote from '@/components/molecules/vendorBonusAmountNote/vendorBonusAmountNote'
import VendorBonusProgressBanner from '@/components/organisms/vendorBonusProgressBanner/vendorBonusProgressBanner'
import { useCheckInternetConnection } from '@/hooks'
import { useTranslate } from '@/i18n/useTranslate'
import { useOrder } from '@/providers'
import { apiService, PosActivateResponse } from '@/services/apiService'
import { getCardPaymentDebugEnabled } from '@/services/deviceConfigurationService'
import type { VendorBonusClaim } from '@/types/pos/depositBonus'
import { getJwtToken } from '@/utilities/paymentHelper'
import { buildPosSlipRequest, PrintSlipRequest } from '@/utilities/posSlipHelper'

function OtherAmountPenIcon() {
    return (
        <svg
            className="card-payment__other-amount-icon"
            width="27"
            height="27"
            viewBox="0 0 27 27"
            fill="none"
            aria-hidden="true">
            <path
                d="M4.3875 21.5125C4.3875 21.5125 4.4875 21.5125 4.525 21.5125L8.1875 21.175C8.7 21.125 9.175 20.9 9.5375 20.5375L23.3625 6.7125C24.0125 6.0625 24.375 5.2 24.375 4.2875C24.375 3.375 24.0125 2.5125 23.3625 1.8625L22.475 0.975C21.175 -0.325 18.9125 -0.325 17.6125 0.975L15.85 2.7375L3.8 14.7875C3.4375 15.15 3.2125 15.625 3.175 16.1375L2.8375 19.8C2.8 20.2625 2.9625 20.7125 3.2875 21.05C3.5875 21.35 3.975 21.5125 4.3875 21.5125ZM20.05 1.8375C20.45 1.8375 20.85 1.9875 21.15 2.3L22.0375 3.1875C22.3375 3.4875 22.5 3.875 22.5 4.2875C22.5 4.7 22.3375 5.1 22.0375 5.3875L20.9375 6.4875L17.85 3.4L18.95 2.3C19.25 2 19.65 1.8375 20.05 1.8375ZM5.0375 16.3125C5.0375 16.2375 5.075 16.175 5.125 16.125L16.5125 4.725L19.6 7.8125L8.2125 19.2C8.2125 19.2 8.0875 19.2875 8.025 19.2875L4.7375 19.5875L5.0375 16.3V16.3125ZM26.875 25.5C26.875 26.0125 26.45 26.4375 25.9375 26.4375H0.9375C0.425 26.4375 0 26.0125 0 25.5C0 24.9875 0.425 24.5625 0.9375 24.5625H25.9375C26.45 24.5625 26.875 24.9875 26.875 25.5Z"
                fill="currentColor"
            />
        </svg>
    )
}

// Two-column grid fills row-by-row; interleave left/right columns so each
// column reads ascending top-to-bottom (same layout as betting amounts).
function interleaveAmountsForGrid(amounts: number[]): number[] {
    const sorted = [...amounts].sort((a, b) => a - b)
    const midpoint = Math.floor(sorted.length / 2)
    const leftColumn = sorted.slice(0, midpoint)
    const rightColumn = sorted.slice(midpoint)
    const interleaved: number[] = []

    for (let i = 0; i < rightColumn.length; i++) {
        if (i < leftColumn.length) {
            interleaved.push(leftColumn[i], rightColumn[i])
        } else {
            interleaved.push(rightColumn[i])
        }
    }

    return interleaved
}

// On-screen POS debug entries are only collected when the device
// configuration enables card-payment debug mode.
interface PosDebugEntry {
    timestamp: string
    label: string
    data: unknown
}

export type CardPaymentProps = {
    onPaymentSuccess: (
        response: PosActivateResponse,
        slip?: PrintSlipRequest
    ) => void
    onPaymentError: (error: string) => void
    onSwitchToCash?: () => void
    onCancelTransaction?: () => void
    accountId?: string
    onChangeSelection?: () => void
    canUsePreviousVoucher?: boolean
    onUsePreviousVoucher?: () => void
    existingDraftAmount?: number
    vendorBonusClaim?: VendorBonusClaim | null
}

export default function CardPayment({
    onPaymentSuccess,
    onPaymentError,
    onSwitchToCash,
    onCancelTransaction,
    accountId,
    onChangeSelection,
    canUsePreviousVoucher = false,
    onUsePreviousVoucher,
    existingDraftAmount = 0,
    vendorBonusClaim
}: CardPaymentProps) {
    const { t } = useTranslate()
    const { state } = useOrder()
    const sessionId = state.sessionId || undefined
    const { deviceConfiguration } = useCheckInternetConnection()
    const debugEnabled = getCardPaymentDebugEnabled(deviceConfiguration)
    const isGaming = state.voucherType === 'gaming'
    const isBetting = state.voucherType === 'betting'
    const selectedVendor = state.selectedBettingVendor
    const basketTitleKey = isBetting
        ? 'cardPayment.selectedService'
        : 'cardPayment.selectedProduct'
    const basketItems = useMemo(
        () => Object.values(state.basket),
        [state.basket]
    )
    const selectedProduct =
        basketItems.length > 0 ? basketItems[0].product : null

    const [amount, setAmount] = useState<string>('')
    const [screenMode, setScreenMode] = useState<
        'selection' | 'processing' | 'failed'
    >('selection')
    const [isCustomAmountModalOpen, setIsCustomAmountModalOpen] =
        useState<boolean>(false)
    const [customAmountInput, setCustomAmountInput] = useState<string>('')
    const [selectedQuickAmount, setSelectedQuickAmount] = useState<
        number | null
    >(null)
    const [isCustomAmountActive, setIsCustomAmountActive] = useState(false)
    const [posSlip, setPosSlip] = useState<PrintSlipRequest | null>(null)

    // On-screen POS debug log — only populated when the device configuration
    // enables card-payment debug mode. Console logging stays unconditional so
    // remote diagnostics still work without the on-screen overlay.
    const [debugLog, setDebugLog] = useState<PosDebugEntry[]>([])
    const debugLogRef = useRef<HTMLPreElement>(null)
    const addDebugEntry = useCallback(
        (label: string, data: unknown) => {
            console.log(`[POS DEBUG] ${label}`, data)
            if (!debugEnabled) return

            const entry: PosDebugEntry = {
                timestamp: new Date().toISOString(),
                label,
                data
            }
            setDebugLog((prev) => [...prev, entry])
            requestAnimationFrame(() => {
                debugLogRef.current?.scrollTo(
                    0,
                    debugLogRef.current.scrollHeight
                )
            })
        },
        [debugEnabled]
    )

    const minAmount = isGaming ? 200 : 500
    const maxAmount = 100000
    const numericAmount = parseFloat(amount) || 0
    const isAmountValid =
        Boolean(amount) &&
        numericAmount >= minAmount &&
        numericAmount <= maxAmount
    const customAmountNumber = parseInt(customAmountInput || '0', 10)
    const customAmountHasValue = Boolean(customAmountInput)
    const isCustomAmountValid =
        customAmountHasValue &&
        customAmountNumber >= minAmount &&
        customAmountNumber <= maxAmount
    const customAmountHasError = customAmountHasValue && !isCustomAmountValid

    const suggestedProductAmount =
        isGaming && selectedProduct?.price ? selectedProduct.price : null
    const quickAmounts = useMemo(() => {
        const baseSortedAmounts = isGaming
            ? [1000, 1500, 2000, 3000, 3500, 7000]
            : [500, 1000, 2000, 5000, 10000, 20000]

        return interleaveAmountsForGrid(baseSortedAmounts)
    }, [isGaming])

    const formatRsd = (value: number) =>
        `${new Intl.NumberFormat('sr-RS').format(value)} RSD`
    const formatAmountParts = (value: number) => ({
        amount: new Intl.NumberFormat('sr-RS').format(value),
        currency: 'RSD'
    })
    const selectedAmountParts = formatAmountParts(numericAmount)

    const basketTotal = basketItems.reduce(
        (total, item) => total + (item.product.price || 0) * item.qty,
        0
    )

    useEffect(() => {
        if (!suggestedProductAmount || amount) {
            return
        }

        setSelectedQuickAmount(null)
        setAmount(suggestedProductAmount.toString())
        setCustomAmountInput(suggestedProductAmount.toString())
        setIsCustomAmountActive(true)
    }, [amount, suggestedProductAmount])

    const handleQuickAmount = (value: number) => {
        setSelectedQuickAmount(value)
        setAmount(value.toString())
        setCustomAmountInput(value.toString())
        setIsCustomAmountActive(false)
    }

    const openCustomAmountModal = () => {
        if (isCustomAmountActive && amount) {
            setCustomAmountInput(amount)
        } else {
            setSelectedQuickAmount(null)
            setAmount('')
            setCustomAmountInput('')
            setIsCustomAmountActive(false)
        }
        setIsCustomAmountModalOpen(true)
    }

    const appendCustomDigit = (digit: number) => {
        setCustomAmountInput((prev) => {
            const nextRaw = `${prev}${digit}`.replace(/^0+(\d)/, '$1')
            const nextNumber = parseInt(nextRaw || '0', 10)
            if (nextNumber > maxAmount) {
                return prev
            }

            return nextRaw
        })
    }

    const clearCustomAmount = () => {
        setCustomAmountInput('')
    }

    const deleteCustomDigit = () => {
        setCustomAmountInput((prev) => prev.slice(0, -1))
    }

    const confirmCustomAmount = () => {
        if (!isCustomAmountValid) {
            return
        }

        setSelectedQuickAmount(null)
        setAmount(customAmountInput)
        setIsCustomAmountActive(true)
        setIsCustomAmountModalOpen(false)
    }

    const handleRetry = () => {
        setScreenMode('selection')
    }

    const handleCancelTransaction = () => {
        // Cancelling a failed card transaction bails out of the entire
        // purchase flow back to the welcome screen rather than rewinding to
        // the amount-selection step.
        if (onCancelTransaction) {
            onCancelTransaction()
            return
        }

        setAmount('')
        setCustomAmountInput('')
        setSelectedQuickAmount(null)
        setIsCustomAmountActive(false)
        setScreenMode('selection')
    }

    const handleConfirmPayment = async () => {
        if (!isAmountValid) {
            return
        }
        setScreenMode('processing')

        try {
            addDebugEntry('getJwtToken()', { status: 'calling...' })
            const jwt = await getJwtToken()
            addDebugEntry('getJwtToken() result', {
                hasToken: Boolean(jwt),
                tokenPreview: jwt ? `${jwt.substring(0, 20)}…` : null
            })

            if (!jwt) {
                addDebugEntry('ABORT', 'No JWT token — cannot proceed')
                setScreenMode('failed')
                onPaymentError('Authentication required. Please try again.')
                return
            }

            const sessionId = state.sessionId || undefined

            const requestPayload = {
                amount: numericAmount.toFixed(2),
                terminalId: isGaming ? '02' : '01',
                jwt_token: `${jwt.substring(0, 20)}…`,
                currency: '941',
                depositTypeId: 'PAYMENT_CARD_TRANSACTION',
                voucher_type_id: state.voucherType === 'betting' ? '30' : '20'
            }
            addDebugEntry('activatePos() request', {
                ...requestPayload,
                sessionId
            })

            const response = await apiService.activatePos(
                {
                    amount: numericAmount.toFixed(2),
                    terminalId: isGaming ? '02' : '01',
                    jwt_token: jwt,
                    currency: '941',
                    depositTypeId: 'PAYMENT_CARD_TRANSACTION',
                    voucher_type_id:
                        state.voucherType === 'betting' ? '30' : '20'
                },
                sessionId
            )

            addDebugEntry('activatePos() response', response)

            // Build the POS slip payload from the activation result so it can be
            // rendered (and manually printed) on both success and failure.
            let slip: PrintSlipRequest | undefined
            try {
                slip = await buildPosSlipRequest(
                    {
                        amount: numericAmount.toFixed(2),
                        currency: '941',
                        terminalId: isGaming ? '02' : '01'
                    },
                    {
                        success: response.success,
                        data: response.data,
                        timestamp: response.timestamp
                    }
                )
                setPosSlip(slip)
            } catch (slipError) {
                addDebugEntry('buildPosSlipRequest() EXCEPTION', slipError)
            }

            if (response.success) {
                addDebugEntry('RESULT', 'Payment SUCCESS — proceeding')
                onPaymentSuccess(response, slip)
            } else {
                addDebugEntry('RESULT', {
                    status: 'FAILED',
                    message: response.message,
                    errorCode: response.errorCode
                })
                setScreenMode('failed')
            }
        } catch (error) {
            const errorInfo =
                error instanceof Error
                    ? { name: error.name, message: error.message }
                    : error
            addDebugEntry('activatePos() EXCEPTION', errorInfo)
            onPaymentError(
                error instanceof Error
                    ? error.message
                    : 'Unknown error occurred'
            )
            setScreenMode('failed')
        }
    }

    // Debug panel persists across all screen modes, but is suppressed when
    // the device configuration has not opted into card-payment debug mode.
    const renderDebugPanel = () => {
        if (!debugEnabled || debugLog.length === 0) {
            return null
        }
        return (
            <div className="card-payment__debug-panel">
                <div className="card-payment__debug-panel-header">
                    <span>POS Debug Log ({debugLog.length})</span>
                    <button type="button" onClick={() => setDebugLog([])}>
                        Clear
                    </button>
                </div>
                <pre
                    ref={debugLogRef}
                    className="card-payment__debug-panel-log">
                    {debugLog
                        .map(
                            (entry) =>
                                `[${entry.timestamp.split('T')[1]}] ${entry.label}\n${JSON.stringify(entry.data, null, 2)}`
                        )
                        .join('\n\n')}
                </pre>
            </div>
        )
    }

    if (screenMode === 'processing') {
        return (
            <div className="card-payment card-payment--processing">
                <h1>{t('cardPayment.processingTitle')}</h1>
                <div className="card-payment__processing-amount">
                    <span>{t('cardPayment.amountToPay')}</span>
                    <strong>{formatRsd(numericAmount)}</strong>
                </div>
                <div className="card-payment__instruction-visual">
                    <img
                        src={cardPaymentInstruction}
                        alt={t('cardPayment.illustrationAlt')}
                    />
                </div>
                <p className="card-payment__processing-subtitle">
                    {t('cardPayment.processingSubtitle')}
                </p>
                <div className="card-payment__supported-cards">
                    <p>{t('cardPayment.supportedCards')}</p>
                    <img
                        className="card-payment__supported-cards-image"
                        src={supportedCards}
                        alt={t('cardPayment.supportedCardsAlt')}
                    />
                </div>
                {renderDebugPanel()}
            </div>
        )
    }

    if (screenMode === 'failed') {
        return (
            <div className="card-payment card-payment--failed">
                <div className="card-payment__failed-icon">
                    <img
                        src={paymentFailedIndicator}
                        alt={t('cardPayment.failedIconAlt')}
                    />
                </div>

                <h1>{t('cardPayment.failedTitle')}</h1>
                <div className="card-payment__failed-amount">
                    <span>{t('cardPayment.cardNotCharged')}</span>
                    <strong>{formatRsd(numericAmount)}</strong>
                </div>

                {posSlip && (
                    <PosReceiptCard slip={posSlip} sessionId={sessionId} />
                )}

                <button
                    type="button"
                    className="card-payment__failed-option card-payment__failed-option--primary"
                    onClick={handleRetry}>
                    <span
                        className="card-payment__failed-option-icon"
                        aria-hidden="true">
                        <img src={cardIcon} alt="" />
                    </span>
                    <span className="card-payment__failed-option-copy">
                        <strong>{t('cardPayment.tryAgain')}</strong>
                        <small>{t('cardPayment.tryAgainSubtitle')}</small>
                    </span>
                    <span className="card-payment__failed-option-arrow">
                        &rarr;
                    </span>
                </button>
                <button
                    type="button"
                    className="card-payment__failed-option card-payment__failed-option--secondary"
                    onClick={onSwitchToCash}>
                    <span
                        className="card-payment__failed-option-icon"
                        aria-hidden="true">
                        <img src={cashIcon} alt="" />
                    </span>
                    <span className="card-payment__failed-option-copy">
                        <strong>{t('cardPayment.switchToCash')}</strong>
                        <small>{t('cardPayment.switchToCashSubtitle')}</small>
                    </span>
                    <span className="card-payment__failed-option-arrow">
                        &rarr;
                    </span>
                </button>

                <button
                    className="card-payment__cancel-transaction"
                    onClick={handleCancelTransaction}>
                    {t('cardPayment.cancelTransaction')}
                </button>
                {renderDebugPanel()}
            </div>
        )
    }

    const showCustomAmountOnButton = isCustomAmountActive && isAmountValid
    const hasExistingDraftAmount = existingDraftAmount > 0
    const totalVoucherAmount = existingDraftAmount + numericAmount
    const showBonusBanner = Boolean(vendorBonusClaim)
    const showBonusOnAmount = Boolean(vendorBonusClaim && numericAmount > 0)

    return (
        <div
            className={`card-payment card-payment--selection ${showBonusBanner ? 'card-payment--with-bonus' : ''}`}>
            <h1>{t('cardPayment.title')}</h1>
            <p className="card-payment__subtitle">
                {t('cardPayment.subtitle')}
            </p>

            <div className="card-payment__amount-card">
                <div className="card-payment__amount-grid">
                    {quickAmounts.map((quickAmount) => {
                        const amountParts = formatAmountParts(quickAmount)
                        return (
                            <button
                                key={quickAmount}
                                type="button"
                                className={`card-payment__amount-tile ${selectedQuickAmount === quickAmount ? 'card-payment__amount-tile--selected' : ''}`}
                                onClick={() => handleQuickAmount(quickAmount)}>
                                <span className="card-payment__amount-tile-value">
                                    {amountParts.amount}
                                    <span>{amountParts.currency}</span>
                                </span>
                            </button>
                        )
                    })}
                </div>
                <button
                    type="button"
                    className={`card-payment__other-amount ${showCustomAmountOnButton ? 'card-payment__other-amount--selected' : ''}`}
                    onClick={openCustomAmountModal}>
                    {showCustomAmountOnButton ? (
                        <>
                            <span className="card-payment__amount-tile-value">
                                {selectedAmountParts.amount}
                                <span>{selectedAmountParts.currency}</span>
                            </span>
                            <OtherAmountPenIcon />
                        </>
                    ) : (
                        <>
                            <OtherAmountPenIcon />
                            {t('cardPayment.otherAmount')}
                        </>
                    )}
                </button>
            </div>

            {vendorBonusClaim ? (
                <VendorBonusProgressBanner claim={vendorBonusClaim} />
            ) : null}

            {amount && !isAmountValid && (
                <div className="card-payment__validation-message">
                    {t('cardPayment.validation', {
                        minAmount,
                        maxAmount: new Intl.NumberFormat('sr-RS').format(
                            maxAmount
                        )
                    })}
                </div>
            )}

            <div className="card-payment__summary">
                <section
                    className={`card-payment__selected-amount-card ${showBonusOnAmount ? 'card-payment__selected-amount-card--with-bonus' : ''}`}>
                    {hasExistingDraftAmount && (
                        <div className="card-payment__existing-voucher">
                            <span className="card-payment__existing-voucher-label">
                                {t('cardPayment.existingVoucherBalance')}
                            </span>
                            <div className="card-payment__existing-voucher-value">
                                {formatAmountParts(existingDraftAmount).amount}
                                <span>{formatAmountParts(existingDraftAmount).currency}</span>
                            </div>
                        </div>
                    )}

                    <span className="card-payment__selected-amount-label">
                        {t('cardPayment.selectedAmount')}
                    </span>
                    <div className="card-payment__selected-amount-value">
                        {selectedAmountParts.amount}
                        <span>{selectedAmountParts.currency}</span>
                    </div>

                    {showBonusOnAmount && vendorBonusClaim ? (
                        <VendorBonusAmountNote
                            vendorName={vendorBonusClaim.vendorName}
                            bonusAmount={vendorBonusClaim.rewardAmount}
                            currencyCode={vendorBonusClaim.currencyCode}
                            depositOrdinal={vendorBonusClaim.depositOrdinal}
                            paymentAmount={numericAmount}
                        />
                    ) : null}

                    {hasExistingDraftAmount && isAmountValid && (
                        <div className="card-payment__total-voucher-amount">
                            <span>{t('cardPayment.totalVoucherAmount')}</span>
                            <strong>{formatRsd(totalVoucherAmount)}</strong>
                        </div>
                    )}

                    {!showBonusOnAmount ? (
                        <p>{t('cardPayment.selectedAmountHint')}</p>
                    ) : null}

                    {canUsePreviousVoucher && (
                        <button
                            type="button"
                            className="card-payment__has-voucher"
                            onClick={onUsePreviousVoucher}>
                            {t('insertCash.hasVoucher')}
                        </button>
                    )}
                </section>

                <section className="card-payment__basket-card">
                    <span className="card-payment__basket-eyebrow">
                        {t(basketTitleKey)}
                    </span>

                    {selectedVendor ? (
                        <>
                            <div className="card-payment__basket-image card-payment__basket-image--vendor">
                                <img
                                    src={
                                        selectedVendor.squareImageUrl ||
                                        vendorPlaceholderDark
                                    }
                                    alt={selectedVendor.name}
                                />
                            </div>
                            <h2>{selectedVendor.name}</h2>
                            {accountId && (
                                <div className="card-payment__basket-account">
                                    <span>
                                        {t('cardPayment.accountNumber')}
                                    </span>
                                    <strong>{accountId}</strong>
                                </div>
                            )}
                            <button
                                type="button"
                                className="card-payment__change-selection"
                                onClick={onChangeSelection}>
                                {t('bettingVendorAccount.changeSelection')}
                            </button>
                        </>
                    ) : selectedProduct ? (
                        <>
                            <div className="card-payment__basket-image">
                                <img
                                    src={
                                        selectedProduct.imageUrl ||
                                        welcome_gaming_playStation
                                    }
                                    alt={selectedProduct.name}
                                />
                            </div>
                            <h2>{selectedProduct.name}</h2>
                            {basketTotal > 0 && (
                                <p className="card-payment__basket-price">
                                    {formatRsd(basketTotal)}
                                </p>
                            )}
                            <button
                                type="button"
                                className="card-payment__change-selection"
                                onClick={onChangeSelection}>
                                {t('bettingVendorAccount.changeSelection')}
                            </button>
                        </>
                    ) : (
                        <div className="card-payment__basket-empty">
                            <img
                                src={noProductsImg}
                                alt={t('insertCash.noSelectedProducts')}
                            />
                            <h2>{t('insertCash.noSelectedProducts')}</h2>
                            <p>{t('insertCash.canTopUpAmount')}</p>
                        </div>
                    )}
                </section>
            </div>

            <div className="buttons-wrapper">
                <PrimaryButton
                    isDisabled={!isAmountValid}
                    text={
                        isAmountValid
                            ? t('cardPayment.payCta', {
                                  amount: formatRsd(numericAmount)
                              })
                            : t('cardPayment.selectAmountCta')
                    }
                    callback={handleConfirmPayment}
                />
            </div>

            {renderDebugPanel()}

            {isCustomAmountModalOpen && (
                <div className="card-payment__modal-backdrop">
                    <div className="card-payment__amount-modal">
                        <button
                            type="button"
                            className="card-payment__amount-modal-close"
                            onClick={() => setIsCustomAmountModalOpen(false)}
                            aria-label={t('common.cancel')}>
                            x
                        </button>

                        <h2 className="card-payment__amount-modal-title">
                            {t('cardPayment.customAmountTitle')}
                        </h2>

                        <div
                            className={`card-payment__amount-modal-display ${customAmountHasError ? 'card-payment__amount-modal-display--invalid' : ''} ${isCustomAmountValid ? 'card-payment__amount-modal-display--valid' : ''}`}>
                            <span>
                                {formatAmountParts(customAmountNumber).amount}
                            </span>
                            <small>RSD</small>
                        </div>

                        <div
                            className={`card-payment__amount-modal-error ${!customAmountHasError ? 'card-payment__amount-modal-error--hidden' : ''}`}>
                            {t('cardPayment.validation', {
                                minAmount,
                                maxAmount: new Intl.NumberFormat(
                                    'sr-RS'
                                ).format(maxAmount)
                            })}
                        </div>

                        <div className="card-payment__amount-modal-limits">
                            {t('cardPayment.amountLimits', {
                                minAmount,
                                maxAmount: new Intl.NumberFormat(
                                    'sr-RS'
                                ).format(maxAmount)
                            })}
                        </div>

                        <div className="card-payment__amount-modal-keypad">
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((digit) => (
                                <button
                                    type="button"
                                    key={digit}
                                    onClick={() => appendCustomDigit(digit)}>
                                    {digit}
                                </button>
                            ))}
                            <button
                                type="button"
                                onClick={deleteCustomDigit}
                                className="card-payment__amount-modal-key card-payment__amount-modal-key--clear">
                                <span aria-hidden="true">⌫</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => appendCustomDigit(0)}>
                                0
                            </button>
                            <button
                                type="button"
                                className="card-payment__amount-modal-key card-payment__amount-modal-key--delete"
                                onClick={clearCustomAmount}>
                                {t('cardPayment.clearAmount')}
                            </button>
                        </div>

                        <button
                            type="button"
                            className="card-payment__amount-modal-confirm"
                            disabled={!isCustomAmountValid}
                            onClick={confirmCustomAmount}>
                            {t('cardPayment.continue')}
                            <span aria-hidden="true">&rarr;</span>
                        </button>
                    </div>
                </div>
            )}
        </div>
    )
}
